import { Area } from './Area'
import { AreaAvailability } from './AreaAvailability'
import { BookingReason } from './BookingReason'
import { BookingSlot } from './BookingSlot'
import { BookingSlotAvailability } from './BookingSlotAvailability'
import { BookingSlotOption } from './BookingSlotOption'
import { Menu } from './Consumable'
import { DiaryNote } from './DiaryNote'
import { Event } from './Event'
import { ProductLibrary } from './ProductLibrary'
import { Venue } from './Venue'

export interface BookingSlotTimeGroup {
    start: Date
    end: Date
    slotCount: number
}

export interface BookingChoicePaymentWarnings {
    cancellationChargesApply: boolean
    depositRequired: boolean
}

export interface BookingReasonChoice extends BookingChoicePaymentWarnings {
    reason: BookingReason
    isFull: boolean
}

export interface BookingEventChoice extends BookingChoicePaymentWarnings {
    event: Event
}

export interface BookingChoiceGroups {
    standardBooking?: BookingChoicePaymentWarnings
    events: BookingEventChoice[]
    reasonsAtIdealTime: BookingReasonChoice[]
    reasonsOutsideIdealTime: BookingReasonChoice[]
}

export interface BookingSlotChoice {
    bookingSlot: BookingSlot
    reservableArea: Area | null
    reservableOption: BookingSlotOption | null
    waitlistArea: Area | null
}

export interface BookingSlotAreaGroup {
    area: Area | null
    slots: BookingSlotChoice[]
    noAvailabilityDescription: string | null
    canJoinWaitlist: boolean
}

export interface BookingUpsellMenuItem {
    imageId: string | null
    menuItemId: string
    refundable?: boolean
    paidOnline?: boolean
    productId: string
    skuId: string
    name: string
    description: string | null
    amount: number
    modifierGroups: BookingUpsellModifierGroup[]
}

export interface BookingUpsellModifierGroup {
    id: string
    name: string
    minimumModifiersRequired: number | null
    maximumModifiersRequired: number | null
    modifiers: { skuId: string; name: string; description: string | null; amount: number }[]
}

export class Availability {

    private defaultMinimumReservableSeats = 2
    private defaultMaximumReservableSeats = 10

    constructor(
        public areas: AreaAvailability[],
        public venue: Venue,
        public productLibrary: ProductLibrary | null,
        public publicDiaryNotes: DiaryNote[]
    ) { }

    get areasSortedByDisplayOrder() {
        return this.areas.sort((a, b) => {
            return a.area.displayOrder - b.area.displayOrder
        })
    }

    hasBookingSlotsInsideCutOffTimeForAreaId(areaId?: string): boolean {
        return this.areasWithBookingSlotsHiddenDueToCutOffTime(areaId).length > 0
    }

    smallestActivePreBookingWindow(areaId?: string): number | null {
        const affectedAreas = this.areasWithBookingSlotsHiddenDueToCutOffTime(areaId)
        if (affectedAreas.length === 0) {
            return null
        }
        const cutOffDurations = affectedAreas
            .map(areaAvailability => {
                return areaAvailability.area.preBookingWindowMinutes
            })
        const smallestCutOffDuration = Math.min(...cutOffDurations)
        if (smallestCutOffDuration === Infinity) {
            return null
        }
        return smallestCutOffDuration
    }

    bookableAreas(): AreaAvailability[] {
        return this.areasSortedByDisplayOrder.filter(areaAvailability => {
            return areaAvailability.areaCanBeBooked
        })
    }

    earliestAvailability(): BookingSlotAvailability | null {
        return this.areas
            .flatMap(areaAvailability => {
                return areaAvailability.bookingSlots
            })
            .filter(bookingSlot => bookingSlot.options.length > 0)
            .sort((a, b) => {
                return a!.bookingSlot.dateTime.getTime() - b!.bookingSlot.dateTime.getTime()
            })[0] ?? null
    }


    earliestBookingSlot(): BookingSlot | null {
        return this.areas
            .map(areaAvailability => {
                return areaAvailability.earliestBookingSlot()
            })
            .filter(bookingSlot => bookingSlot !== null)
            .sort((a, b) => {
                return a!.dateTime.getTime() - b!.dateTime.getTime()
            })[0] ?? null
    }

    latestBookingSlot(): BookingSlot | null {
        return this.areas
            .map(areaAvailability => {
                return areaAvailability.latestBookingSlot()
            })
            .filter(bookingSlot => bookingSlot !== null)
            .sort((a, b) => {
                return b!.dateTime.getTime() - a!.dateTime.getTime()
            })[0] ?? null
    }

    get intervalIdealTimes(): Date[] {
        const start = this.earliestBookingSlot()?.dateTime
        if (!start) {
            return []
        }
        const end = this.latestBookingSlot()?.dateTime
        if (!end) {
            return []
        }
        const bookingInterval = this.venue.bookingInterval
        const times: Date[] = []
        let startTime = new Date(start)
        for (let time = startTime; time <= end; time.setMinutes(time.getMinutes() + bookingInterval)) {
            if (!this.hasBookingSlotAtDate(time)) {
                continue
            }
            times.push(new Date(time))
        }
        return times
    }

    closestIntervalIdealTime(time: Date, date: Date): Date | null {
        const timeOnDate = new Date(date)
        timeOnDate.setHours(time.getHours(), time.getMinutes(), 0, 0)
        return [...this.intervalIdealTimes]
            .sort((first, second) => {
                const firstDifference = Math.abs(first.getTime() - timeOnDate.getTime())
                const secondDifference = Math.abs(second.getTime() - timeOnDate.getTime())
                return firstDifference - secondDifference
            })[0] ?? null
    }

    defaultIdealTime(date: Date, now: Date): Date {
        const earliestBookingSlot = this.earliestAvailability()?.bookingSlot ?? null
        if (earliestBookingSlot !== null) {
            return new Date(earliestBookingSlot.dateTime)
        }
        const defaultTime = new Date(date)
        defaultTime.setHours(now.getHours(), now.getMinutes(), 0, 0)
        if (!this.venue.hidesClosedBookingSlots) {
            defaultTime.setMinutes(0, 0, 0)
        }
        return defaultTime
    }

    hasBookingSlotAtDate(date: Date): boolean {
        const bookingSlot = new BookingSlot(date)
        return this.areas.some(areaAvailability => {
            return areaAvailability.hasBookingSlot(bookingSlot)
        })
    }

    largePartySizeThreshold(date: Date | null, eventId?: string | null): number | null {
        const event = eventId ? this.venue.eventWithId(eventId) : null
        const threshold = event?.minLargePartySize ?? this.venue.minLargePartySize
        if (this.venue.id === '3e6ffcbc-ff1d-48fe-b513-48de406c08d2') { // Zambretto
            if (date?.getMonth() === 11) {
                return 7
            }
        }
        if (this.venue.id === '11170d6d-9ff9-4933-8742-8f6da2f99f0d') { // Salt Lanark
            if (date && date.getDay() >= 5) { // Friday and Saturday
                return 8
            }
        }
        return threshold
    }

    isLargeParty(partySize: number, date: Date | null, eventId?: string | null): boolean {
        const threshold = this.largePartySizeThreshold(date, eventId)
        return threshold !== null && partySize >= threshold
    }

    possiblePartySizes(
        date: Date | null,
        restrictedReasonIds?: string[],
        eventId?: string | null
    ): number[] {
        const min = this.minimumReservablePartySize()
        let max = this.maximumReservablePartySize(date, eventId)
        const restrictedReasons = this.venue.reasons.filter(reason =>
            restrictedReasonIds?.includes(reason.id) ?? false
        )
        if (restrictedReasons.length > 0) {
            const allReasonsHaveMax = restrictedReasons.every(
                reason => reason.maximumPartySize !== null
            )
            if (allReasonsHaveMax) {
                const reasonMaxSizes = restrictedReasons.map(reason => reason.maximumPartySize as number)
                const largestPossibleSize = Math.max(...reasonMaxSizes)
                max = Math.min(max, largestPossibleSize)
            }
        }
        if (max < min) {
            return []
        }
        return Array.from({ length: max - min + 1 }, (_, index) => index + min)
    }

    minimumReservablePartySize(): number {
        return Math.min(
            this.minimumBookingSlotPartySize(),
            ...this.areas.flatMap(areaAvailability => {
                return areaAvailability.area.minimumReservableSeats()
            })
        )
    }

    defaultReservablePartySize(): number {
        return Math.max(
            this.defaultMinimumReservableSeats,
            this.minimumBookingSlotPartySize()
        )
    }

    maximumReservablePartySize(date: Date | null, eventId?: string | null): number {
        const max = Math.max(
            this.maximumBookingSlotPartySize(),
            ...this.areas.flatMap(areaAvailability => {
                return areaAvailability.area.maximumReservableSeats()
            })
        )
        const largePartySizeThreshold = this.largePartySizeThreshold(date, eventId)
        if (largePartySizeThreshold !== null) {
            return Math.min(max, largePartySizeThreshold - 1)
        }
        return max
    }

    isConfigurationReservable(
        partySize: number,
        reasonId: string | null,
        eventId: string | null
    ): boolean {
        return this.areas.some(areaAvailability => {
            return this.areaIsReservableWithConfiguration(
                areaAvailability.area.id,
                partySize,
                reasonId,
                eventId
            )
        })
    }

    isConfiguredForAnyReason(): boolean {
        return this.venue.reasons.length > 0 && this.areas.some(areaAvailability => {
            return areaAvailability.bookingSlots.some(bookingSlotAvailability => {
                return bookingSlotAvailability.options.some(option => {
                    return option.reasonId !== null
                })
            })
        })
    }

    doesConfigurationHaveAnyReservableReasons(partySize: number, idealTime: Date): boolean {
        return this.venue.reasonsUsedOnDate(idealTime).some(reason => {
            return this.areas.some(areaAvailability => {
                return this.areaIsReservableWithConfiguration(
                    areaAvailability.area.id,
                    partySize,
                    reason.id,
                    null
                )
            })
        })
    }

    areaIsConfiguredForChoices(
        areaId: string,
        reasonId: string | null,
        eventId: string | null
    ): boolean {
        const areaAvailability = this.areas.find(areaAvailability => {
            return areaAvailability.area.id === areaId
        })
        if (!areaAvailability) {
            return false
        }
        if (reasonId) {
            const reason = this.venue.reasonWithId(reasonId)
            if (!reason) {
                return false
            }
            if (!areaAvailability.isConfiguredForReason(reason)) {
                return false
            }
        }
        if (eventId) {
            const event = this.venue.eventWithId(eventId)
            if (!event) {
                return false
            }
            if (!areaAvailability.isConfiguredForEvent(event)) {
                return false
            }
        }
        return true
    }

    areaIsReservableWithConfiguration(
        areaId: string,
        partySize: number,
        reasonId: string | null,
        eventId: string | null
    ): boolean {
        const areaAvailability = this.areas.find(areaAvailability => {
            return areaAvailability.area.id === areaId
        })
        if (!areaAvailability) {
            return false
        }
        const reason = reasonId ? this.venue.reasonWithId(reasonId) : null
        const event = eventId ? this.venue.eventWithId(eventId) : null
        return areaAvailability.isReservableWithConfiguration(partySize, reason, event)
    }

    areaIsReservableWithAnyReason(
        areaId: string,
        partySize: number,
        eventId: string | null
    ): boolean {
        const areaAvailability = this.areas.find(areaAvailability => {
            return areaAvailability.area.id === areaId
        })
        if (!areaAvailability) {
            return false
        }
        const event = eventId ? this.venue.eventWithId(eventId) : null
        return areaAvailability.isReservableWithAnyReason(partySize, event)
    }

    isConfigurationReservableWithAnyReason(
        partySize: number,
        eventId: string | null
    ): boolean {
        return this.areas.some(areaAvailability => {
            return this.areaIsReservableWithAnyReason(
                areaAvailability.area.id,
                partySize,
                eventId
            )
        })
    }

    hasReservableReasonOptionAtDateTime(
        reasonId: string,
        partySize: number,
        eventId: string | null,
        dateTime: Date
    ): boolean {
        return this.firstReservableAreaWithConfigurationAtDateTime(
            partySize,
            reasonId,
            eventId,
            dateTime
        ) !== null
    }

    firstReservableAreaWithConfigurationAtDateTime(
        partySize: number,
        reasonId: string | null,
        eventId: string | null,
        dateTime: Date
    ): Area | null {
        const reason = reasonId ? this.venue.reasonWithId(reasonId) : null
        const event = eventId ? this.venue.eventWithId(eventId) : null
        return this.areaAvailabilitiesOrderedForReason(reason)
            .find(areaAvailability => {
                return areaAvailability.reservableOptionWithConfiguration(
                    partySize,
                    reason,
                    event,
                    dateTime
                ) !== null
            })
            ?.area ?? null
    }

    firstReservableAreaWithAnyReasonAtDateTime(
        partySize: number,
        eventId: string | null,
        dateTime: Date
    ): Area | null {
        const event = eventId ? this.venue.eventWithId(eventId) : null
        return this.areaAvailabilitiesOrderedForReason(null)
            .find(areaAvailability => {
                return areaAvailability.reservableOptionWithAnyReason(
                    partySize,
                    event,
                    dateTime
                ) !== null
            })
            ?.area ?? null
    }

    availableEvents(): Event[] {
        return this.venue.events.filter(event => {
            return this.areas.some(areaAvailability => {
                return areaAvailability.bookingSlots.some(bookingSlotAvailability => {
                    return bookingSlotAvailability.options.some(option => {
                        return option.eventId === event.id
                    })
                })
            })
        })
    }

    bookingChoiceGroups(partySize: number, idealTime: Date): BookingChoiceGroups {
        const reasons = this.bookingReasonsApplicableToSelection(partySize, idealTime)
        const events = this.availableEvents()
        const options = this.areas
            .flatMap(area => area.bookingSlots)
            .flatMap(slot => slot.options)
            .filter(option => option.partySize === partySize)
        const standardOptions = this.areas
            .flatMap(area => area.bookingSlots
                .filter(slot => slot.bookingSlot.dateTime.getTime() >= area.area.cutOffTime.getTime())
                .flatMap(slot => slot.options))
            .filter(option => option.partySize === partySize
                && option.reasonId === null && option.eventId === null)
        const reasonIdsAtIdealTime = new Set(
            this.venue.reasonsUsedOnDateTime(idealTime).map(reason => reason.id)
        )
        const choiceFor = (reason: BookingReason): BookingReasonChoice => ({
            reason,
            isFull: this.hasReasonReachedLimit(reason, partySize),
            ...this.bookingChoicePaymentWarnings(options.filter(option => option.reasonId === reason.id)),
        })
        return {
            standardBooking: !this.venue.reasonsUsedOnDate(idealTime).some(reason => reason.isActive)
                && standardOptions.length > 0
                && this.hasReservableStandardBookingOptions()
                ? this.bookingChoicePaymentWarnings(standardOptions)
                : undefined,
            events: events.map(event => ({
                event,
                ...this.bookingChoicePaymentWarnings(options.filter(option => option.eventId === event.id)),
            })),
            reasonsAtIdealTime: reasons
                .filter(reason => reasonIdsAtIdealTime.has(reason.id))
                .map(choiceFor),
            reasonsOutsideIdealTime: reasons
                .filter(reason => !reasonIdsAtIdealTime.has(reason.id))
                .map(choiceFor),
        }
    }

    bookingSlotAreaGroups(
        partySize: number,
        reasonId: string | null,
        eventId: string | null,
        requiresDogFriendly: boolean,
        idealTime: Date | null = null,
        areaId: string | null = null
    ): BookingSlotAreaGroup[] {
        const reason = reasonId ? this.venue.reasonWithId(reasonId) : null
        const event = eventId ? this.venue.eventWithId(eventId) : null
        const areaAvailabilities = this.areaAvailabilitiesOrderedForReason(reason)
            .filter(areaAvailability => !areaAvailability.area.isHardcodedToBeHidden())
            .filter(areaAvailability => {
                return this.areaIsConfiguredForChoices(
                    areaAvailability.area.id,
                    reasonId,
                    eventId
                )
            })
            .filter(areaAvailability => {
                return areaId === null || areaAvailability.area.id === areaId
            })
        const customerChoosesArea = (areaId !== null && areaAvailabilities.length > 0)
            || (this.venue.customerCanChooseAreaFor(reason)
                && areaAvailabilities.length > 1)
        if (customerChoosesArea) {
            return areaAvailabilities.map(areaAvailability => {
                return this.bookingSlotAreaGroup(
                    areaAvailability.area,
                    [areaAvailability],
                    partySize,
                    reason,
                    event,
                    requiresDogFriendly,
                    idealTime
                )
            })
        }
        return [this.bookingSlotAreaGroup(
            null,
            areaAvailabilities,
            partySize,
            reason,
            event,
            requiresDogFriendly,
            idealTime
        )]
    }

    shouldShowBookingUpsell(reasonId: string | null, eventId: string | null = null): boolean {
        const items = this.bookingUpsellMenuItems(reasonId, eventId)
        const account = this.venue.paymentsAccountForCapability('DepositPayments')
        if (this.bookingUpsellMenu(reasonId, eventId)?.isPaid && items.some(item => item.paidOnline !== false)
            && (!account || account.usesBreathePay())) {
            return false
        }
        return items.length > 0
    }

    bookingUpsellMenu(reasonId: string | null, eventId: string | null = null): Menu | null {
        if (eventId !== null || this.productLibrary === null) {
            return null
        }
        const preorderMenuId = reasonId === null
            ? this.venue.preorderMenuId : this.venue.reasonWithId(reasonId)?.preorderMenuId
        return preorderMenuId ? this.productLibrary.getMenu(preorderMenuId) : null
    }

    bookingUpsellMenuItems(reasonId: string | null, eventId: string | null = null): BookingUpsellMenuItem[] {
        const menu = this.bookingUpsellMenu(reasonId, eventId)
        const productLibrary = this.productLibrary
        if (menu === null || productLibrary === null) {
            return []
        }
        return menu.menuCourses.flatMap(course => {
            return course.menuItems.flatMap(menuItem => {
                const consumable = productLibrary.getConsumable(menuItem.consumableId)
                const sku = consumable?.getSKUWithId(menuItem.skuId)
                if (!sku?.onSale) {
                    return []
                }
                return {
                    menuItemId: menuItem.id,
                    refundable: menuItem.refundable,
                    paidOnline: menuItem.paidOnline,
                    productId: menuItem.consumableId,
                    skuId: menuItem.skuId,
                    name: consumable?.displayName ?? 'Item',
                    description: consumable?.description ?? null,
                    imageId: consumable?.imageId ?? null,
                    amount: sku?.price ?? 0,
                    modifierGroups: (consumable?.modifierGroupIds ?? []).flatMap(id => {
                        const group = productLibrary.getModifierGroup(id)
                        return group ? [{
                            id: group.id,
                            name: group.name,
                            minimumModifiersRequired: group.minimumModifiersRequired,
                            maximumModifiersRequired: group.maximumModifiersRequired,
                            modifiers: group.modifiers.flatMap(modifier => {
                                const modifierSku = modifier.skus[0]
                                return modifierSku ? [{
                                    skuId: modifierSku.id,
                                    name: modifier.displayName,
                                    description: modifier.description,
                                    amount: modifierSku.price ?? 0,
                                }] : []
                            }),
                        }] : []
                    }),
                }
            })
        })
    }

    private bookingSlotAreaGroup(
        area: Area | null,
        areaAvailabilities: AreaAvailability[],
        partySize: number,
        reason: BookingReason | null,
        event: Event | null,
        requiresDogFriendly: boolean,
        idealTime: Date | null
    ): BookingSlotAreaGroup {
        const slots = this.bookingSlotChoices(
            areaAvailabilities,
            partySize,
            reason,
            event
        )
        return {
            area,
            slots,
            noAvailabilityDescription: slots.some(slot => slot.reservableOption !== null)
                ? null
                : this.noAvailabilityDescription(
                    requiresDogFriendly,
                    partySize,
                    area?.id ?? null,
                    event?.id ?? null,
                    reason?.id ?? null
                ),
            canJoinWaitlist: this.shouldShowJoinWaitlistButton(
                idealTime,
                area?.id ?? null,
                reason?.id ?? null
            ),
        }
    }

    private bookingChoicePaymentWarnings(
        matchingOptions: BookingSlotOption[]
    ): BookingChoicePaymentWarnings {
        return {
            cancellationChargesApply: matchingOptions.some(option => {
                return option.cancellationChargeAmount !== null
            }),
            depositRequired: matchingOptions.some(option => option.depositAmount !== null),
        }
    }

    private bookingReasonsApplicableToSelection(
        partySize: number,
        idealTime: Date
    ): BookingReason[] {
        const reasons = this.venue.reasonsUsedOnDate(idealTime).filter(reason => reason.isActive)
        const hasReasonAvailability = this.doesConfigurationHaveAnyReservableReasons(
            partySize,
            idealTime
        )
        const hasLargePartyReasonMessage = reasons.some(reason => {
            return reason.maximumPartySize !== null
                && reason.largePartyNoAvailabilityMessage !== null
        })
        const hasExceptionMessage = this.exceptionNoAvailabilityDescription(null) !== null
        return !hasExceptionMessage && (hasReasonAvailability || hasLargePartyReasonMessage)
            ? reasons
            : []
    }

    private bookingSlotChoices(
        areaAvailabilities: AreaAvailability[],
        partySize: number,
        reason: BookingReason | null,
        event: Event | null
    ): BookingSlotChoice[] {
        const bookingSlots = areaAvailabilities
            .flatMap(areaAvailability => areaAvailability.bookingSlots)
            .map(slotAvailability => slotAvailability.bookingSlot)
            .filter((bookingSlot, index, slots) => {
                return slots.findIndex(other => other.equals(bookingSlot)) === index
            })
            .sort((first, second) => {
                return first.dateTime.getTime() - second.dateTime.getTime()
            })
        return bookingSlots.flatMap(bookingSlot => {
            const possibleAreas = areaAvailabilities.filter(areaAvailability => {
                return bookingSlot.dateTime.getTime() >= areaAvailability.area.cutOffTime.getTime()
                    && areaAvailability.hasBookingSlot(bookingSlot)
            })
            if (possibleAreas.length === 0) {
                return []
            }
            const reservableArea = possibleAreas.find(areaAvailability => {
                return areaAvailability.reservableOptionWithConfiguration(
                    partySize,
                    reason,
                    event,
                    bookingSlot.dateTime
                ) !== null
            }) ?? null
            const waitlistArea = reservableArea === null && this.venue.waitlistsEnabled
                ? possibleAreas.find(areaAvailability => {
                    return areaAvailability.bookingSlots.some(slotAvailability => {
                        return slotAvailability.bookingSlot.equals(bookingSlot)
                            && slotAvailability.isWaitlistOnlyWithConfiguration(
                                partySize,
                                reason,
                                event
                            )
                    })
                }) ?? null
                : null
            return [{
                bookingSlot,
                reservableArea: reservableArea?.area ?? null,
                reservableOption: reservableArea?.reservableOptionWithConfiguration(
                    partySize,
                    reason,
                    event,
                    bookingSlot.dateTime
                ) ?? null,
                waitlistArea: waitlistArea?.area ?? null,
            }]
        })
    }

    reservableEvents(): Event[] {
        return this.venue.events.filter(event => {
            return this.areas.some(areaAvailability => {
                return areaAvailability.bookingSlots.some(bookingSlotAvailability => {
                    return bookingSlotAvailability.options.some(option => {
                        return option.eventId === event.id && option.reservableTableCount > 0
                    })
                })
            })
        })
    }

    bookingSlotTimesForEvent(eventId: string): Date[] {
        return this.areas
            .flatMap(areaAvailability => {
                return areaAvailability.bookingSlots
            })
            .filter(bookingSlotAvailability => {
                return bookingSlotAvailability.options.some(option => {
                    return option.eventId === eventId
                })
            })
            .map(bookingSlotAvailability => {
                return new Date(bookingSlotAvailability.bookingSlot.dateTime)
            })
            .filter((dateTime, index, array) => {
                return array.findIndex(other => {
                    return other.getTime() === dateTime.getTime()
                }) === index
            })
            .sort((a, b) => {
                return a.getTime() - b.getTime()
            })
    }

    bookingSlotTimeGroupsForEvent(eventId: string): BookingSlotTimeGroup[] {
        const slotTimes = this.bookingSlotTimesForEvent(eventId)
        const bookingIntervalMilliseconds = this.venue.bookingInterval * 60 * 1000
        return slotTimes.reduce((groups, slotTime) => {
            const previousGroup = groups[groups.length - 1]
            const canJoinPreviousGroup = previousGroup !== undefined
                && bookingIntervalMilliseconds > 0
                && slotTime.getTime() - previousGroup.end.getTime() === bookingIntervalMilliseconds
            if (canJoinPreviousGroup) {
                previousGroup.end = slotTime
                previousGroup.slotCount += 1
            } else {
                groups.push({
                    start: slotTime,
                    end: slotTime,
                    slotCount: 1,
                })
            }
            return groups
        }, [] as BookingSlotTimeGroup[])
    }

    hasReservableStandardBookingOptions(): boolean {
        const hasNonEventOptions = this.areas.some(areaAvailability => {
            return areaAvailability.bookingSlots.some(bookingSlotAvailability => {
                return bookingSlotAvailability.options.some(option => {
                    return option.eventId === null
                })
            })
        })
        const eventIds = this.configuredEventIds()
        if (eventIds.length === 0) {
            return hasNonEventOptions
        }
        const exclusiveEvents = this.venue.events
            .filter(event => event.isExclusive)
            .filter(event => eventIds.includes(event.id))
        const optionsPerBookingSlot = this.allAreaOptionsPerBookingSlot()
        const everyBookingSlotHasExclusiveEvent = optionsPerBookingSlot.every(options => {
            const noStandardOptionsSoEffectivelyExclusive = options.length === 0
            if (noStandardOptionsSoEffectivelyExclusive) {
                return true
            }
            return options.some(option => {
                return exclusiveEvents.some(event => {
                    return option.eventId === event.id
                })
            })
        })
        if (everyBookingSlotHasExclusiveEvent) {
            return false
        }
        return hasNonEventOptions
    }

    reservableOptionWithConfiguration(
        areaId: string,
        partySize: number,
        reasonId: string | null,
        eventId: string | null,
        dateTime: Date
    ): BookingSlotOption | null {
        const areaAvailability = this.getAvailabilityByAreaId(areaId)
        if (!areaAvailability) {
            return null
        }
        const reason = reasonId ? this.venue.reasonWithId(reasonId) : null
        const event = eventId ? this.venue.eventWithId(eventId) : null
        return areaAvailability.reservableOptionWithConfiguration(
            partySize,
            reason,
            event,
            dateTime
        )
    }

    noAvailabilityDescription(
        requiresDogFriendly: boolean | null,
        partySize: number,
        selectedAreaId: string | null,
        selectedEventId: string | null,
        selectedReasonId: string | null
    ): string | null {
        const noAvailabilityBecausePaused = this.venue.bookingsClosedUntilDate !== null
        if (noAvailabilityBecausePaused) {
            return this.venue.bookingsPausedNoAvailabilityMessage
        }
        const exceptionDescription = this.exceptionNoAvailabilityDescription(selectedAreaId)
        if (exceptionDescription) {
            return exceptionDescription
        }
        if (requiresDogFriendly === true && this.venue.dogFriendlyNoAvailabilityMessage !== null) {
            return this.venue.dogFriendlyNoAvailabilityMessage
        }
        if (partySize && selectedReasonId) {
            const reason = this.venue.reasonWithId(selectedReasonId)
            if (reason && reason.maximumPartySize !== null && partySize > reason.maximumPartySize) {
                if (reason.largePartyNoAvailabilityMessage) {
                    return reason.largePartyNoAvailabilityMessage
                }
            }
        }
        if (partySize && selectedEventId) {
            const event = this.venue.eventWithId(selectedEventId)
            if (event && !event.isPartySizeAllowed(partySize)) {
                const eventIsReservableWithOtherPartySizes = this.reservableEvents()
                    .find(event => event.id === selectedEventId) !== undefined
                if (eventIsReservableWithOtherPartySizes) {
                    if (event.partySizeRestrictedMessage) {
                        return event.partySizeRestrictedMessage
                    }
                }
            }
        }
        const noAvailabilityBecauseClosed = this.allAreasAreClosed()
        if (noAvailabilityBecauseClosed) {
            return this.venue.venueClosedNoAvailabilityMessage ?? this.venue.noBookingSlotAvailableMessage
        }
        return this.venue.noBookingSlotAvailableMessage
    }

    exceptionNoAvailabilityDescription(selectedAreaId: string | null): string | null {
        if (selectedAreaId) {
            const selectedAreaSpecificExceptionDescription = this.getAvailabilityByAreaId(selectedAreaId)?.exceptionDescription
            if (this.hasExceptionDescription(selectedAreaSpecificExceptionDescription)) {
                return selectedAreaSpecificExceptionDescription
            }
            return null
        }
        return this.exceptionDescriptionForAllRelevantAreas()
    }

    private hasExceptionDescription(description: string | null | undefined): description is string {
        return description !== null &&
            description !== undefined &&
            description.trim().length > 0
    }

    private exceptionDescriptionForAllRelevantAreas(): string | null {
        const relevantAreas = this.areasSortedByDisplayOrder
            .filter(areaAvailability => {
                return this.hasExceptionDescription(areaAvailability.exceptionDescription) ||
                    areaAvailability.bookingSlots.some(bookingSlotAvailability => {
                        return bookingSlotAvailability.options.length > 0
                    })
            })
        if (relevantAreas.length === 0) {
            return null
        }
        const everyRelevantAreaHasExceptionDescription = relevantAreas.every(areaAvailability => {
            return this.hasExceptionDescription(areaAvailability.exceptionDescription)
        })
        if (!everyRelevantAreaHasExceptionDescription) {
            return null
        }
        return relevantAreas[0].exceptionDescription
    }

    shouldShowJoinWaitlistButton(
        idealTime: Date | null,
        selectedAreaId: string | null,
        selectedReasonId: string | null
    ): boolean {
        if (!this.venue.waitlistsEnabled) {
            return false
        }
        if (this.scheduleExceptionNoAvailabilityDescriptionIsShown(selectedAreaId)) {
            return false
        }
        if (selectedReasonId !== null && idealTime !== null) {
            return this.venue.reasonsUsedOnDateTime(idealTime)
                .some(reason => reason.id === selectedReasonId)
        }
        return true
    }

    private scheduleExceptionNoAvailabilityDescriptionIsShown(selectedAreaId: string | null): boolean {
        const noAvailabilityBecausePaused = this.venue.bookingsClosedUntilDate !== null
        if (noAvailabilityBecausePaused) {
            return false
        }
        return this.exceptionNoAvailabilityDescription(selectedAreaId) !== null
    }

    notReservableOnlineMessage(eventId: string | null): string | null {
        if (!eventId) {
            return null
        }
        const event = this.venue.eventWithId(eventId)
        if (!event) {
            return null
        }
        return event.notReservableOnlineMessage
    }

    hasARestrictedTable(): boolean {
        return this.hasAWheelchairRestrictedTable() || this.hasADogRestrictedTable() || this.hasAPramRestrictedTable() || this.hasAHighchairRestrictedTable()
    }

    hasAWheelchairRestrictedTable(): boolean {
        return this.areas.some(areaAvailability => {
            return areaAvailability.area.hasAWheelchairRestrictedTable()
        })
    }

    hasADogRestrictedTable(): boolean {
        if (this.exclusiveEventsDenyOpportunityForDogFriendlyBookings()) {
            return false
        }
        return this.areas.some(areaAvailability => {
            return areaAvailability.area.hasADogRestrictedTable()
        })
    }

    hasAPramRestrictedTable(): boolean {
        return this.areas.some(areaAvailability => {
            return areaAvailability.area.hasAPramRestrictedTable()
        })
    }

    hasAHighchairRestrictedTable(): boolean {
        return this.areas.some(areaAvailability => {
            return areaAvailability.area.hasAHighchairRestrictedTable()
        })
    }

    private exclusiveEventsDenyOpportunityForDogFriendlyBookings() {
        const eventIds = this.configuredEventIds()
        if (eventIds.length === 0) {
            return false
        }
        const exclusiveNoDogAllowedEvents = this.venue.events
            .filter(event => event.isExclusive)
            .filter(event => event.dogFriendlyNotAllowed)
            .filter(event => eventIds.includes(event.id))
        const optionsPerBookingSlot = this.allAreaOptionsPerBookingSlot()
        const everyBookingSlotHasExclusiveNoDogAllowedEvent = optionsPerBookingSlot.every(options => {
            const noStandardOptionsSoEffectivelyExclusive = options.length === 0
            if (noStandardOptionsSoEffectivelyExclusive) {
                return true
            }
            return options.some(option => {
                return exclusiveNoDogAllowedEvents.some(event => {
                    return option.eventId === event.id
                })
            })
        })
        if (everyBookingSlotHasExclusiveNoDogAllowedEvent) {
            return true
        }
        return false
    }

    publicDiaryNoteOnDate(dateTime: Date): string | null {
        // First check for dynamic public diary notes from the backend
        const matchingDiaryNote = this.publicDiaryNotes.find(note => note.occursOnDate(dateTime))
        if (matchingDiaryNote) {
            return matchingDiaryNote.note
        }

        // Fall back to hardcoded notes for backwards compatibility
        const orchardPubQuizEventId = '39527032-5a04-48b4-ac16-b5e5c9a904ec'
        if (this.availableEvents().some(event => event.id === orchardPubQuizEventId)) {
            return 'There will be a pub quiz starting at 7.30pm tonight'
        }
        const orchardVenueId = '06c3abe8-2ce4-4535-a07f-1c65d6836104'
        if (this.venue.id === orchardVenueId) {
            const date = new Date(dateTime)
            if (date.getMonth() === 10 && date.getDate() === 16 && date.getFullYear() === 2025) {
                return 'We will be showing the Scotland v Argentina rugby game between 2pm and 5pm'
            }
        }
        const ccBloomsVenueId = 'e69e1c31-4457-4ef0-a83b-83463235b03e'
        if (this.venue.id === ccBloomsVenueId) {
            const date = new Date(dateTime)
            const isSunday = date.getDay() === 0
            if (isSunday) {
                return 'Please be advised that Sundays from 8pm until 11pm we host an interactive drag/cabaret show.\n The Rear Area and Bar Area are best for watching the show, with the Front Area less noisy and more intimate.'
            }
        }
        const salthouseVenueId = '339abc70-0f63-42ae-9540-8ef3b02221c1'
        if (this.venue.id === salthouseVenueId) {
            const date = new Date(dateTime)
            if (date.getMonth() === 10 && date.getDate() === 27 && date.getFullYear() === 2025) {
                return 'Backworth choir performance at 6:30pm'
            }
        }
        const captainDarlingVenueId = '5ed2e2ba-016b-4fca-ab10-7dace40979fe'
        if (this.venue.id === captainDarlingVenueId) {
            const date = new Date(dateTime)
            if ((date.getMonth() === 11 && date.getDate() === 26) ||
                (date.getMonth() === 0 && date.getDate() === 1)) {
                return 'We are serving our Sunday Roast menu all day on Boxing Day and New Year\'s Day. Please see our <a href="https://static1.squarespace.com/static/67c0ee415cf6f511e428b5e1/t/6854a90f41659b60c1a13561/1750378767186/TCD-Sunday-Lunch-menu_190625.pdf" target="_blank">website for more details</a>.'
            }
            if ((date.getMonth() === 11 && date.getDate() === 31 && date.getFullYear() === 2025)) {
                return 'We will be closing before the bells at 11pm.'
            }
        }
        return null
    }

    allAreasAreClosed(): boolean {
        const hasReservableTables = this.areas.some(areaAvailability => {
            return areaAvailability.area.hasAReservableTable()
        })
        // If there are no reservable tables, we are not neccessarily closed, we just have no availability
        if (!hasReservableTables) {
            return false
        }
        return this.areas.every(areaAvailability => !areaAvailability.areaCanBeBooked)
    }

    areasUsedInEvent(event: Event): Area[] {
        return this.areas
            .filter(areaAvailability => {
                return areaAvailability.bookingSlots.some(bookingSlotAvailability => {
                    return bookingSlotAvailability.options.some(option => {
                        return option.eventId === event.id
                    })
                })
            })
            .map(areaAvailability => areaAvailability.area)
    }

    hasReasonReachedLimit(
        reason: BookingReason,
        partySize: number
    ): boolean {
        const doesReasonHaveALimit = reason.dailyCoverLimit !== null && reason.dailyCoverLimit > 0
        if (!doesReasonHaveALimit) {
            return false
        }
        const hasReservableOptions = this.areas.some(areaAvailability => {
            return areaAvailability.isReservableWithConfiguration(partySize, reason, null)
        })
        if (hasReservableOptions) {
            return false
        }
        return true
    }

    getDateTimeForBookingSlotDateAndTime(
        date: Date,
        time: Date
    ): Date {
        let dateTime = new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            time.getHours(),
            time.getMinutes()
        )
        const earliestBookingSlot = this.earliestBookingSlot()
        if (earliestBookingSlot && dateTime.getTime() < earliestBookingSlot.dateTime.getTime()) {
            dateTime = new Date(
                dateTime.getFullYear(),
                dateTime.getMonth(),
                dateTime.getDate() + 1,
                dateTime.getHours(),
                dateTime.getMinutes()
            )
        }
        return dateTime
    }

    private minimumBookingSlotPartySize(): number {
        const minimum = Math.min(
            ...this.areas.flatMap(areaAvailability => {
                return areaAvailability.bookingSlots.flatMap(bookingSlotAvailability => {
                    return bookingSlotAvailability.options.map(option => {
                        return option.partySize
                    })
                })
            })
        )
        if (minimum === Infinity) {
            return this.defaultMinimumReservableSeats
        }
        return minimum
    }

    private maximumBookingSlotPartySize(): number {
        const maximum = Math.max(
            ...this.areas.flatMap(areaAvailability => {
                return areaAvailability.bookingSlots.flatMap(bookingSlotAvailability => {
                    return bookingSlotAvailability.options.map(option => {
                        return option.partySize
                    })
                })
            })
        )
        if (maximum === Infinity) {
            return this.defaultMaximumReservableSeats
        }
        return maximum
    }

    private getAvailabilityByAreaId(selectedAreaId: string) {
        return this.areas.find(areaAvailability => {
            return areaAvailability.area.id === selectedAreaId
        }) ?? null
    }

    areaAvailabilitiesOrderedForReason(reason: BookingReason | null): AreaAvailability[] {
        const areaBookingOrder = reason?.areaBookingOrder
        if (!areaBookingOrder?.length) {
            return [...this.areas].sort((a, b) => a.area.displayOrder - b.area.displayOrder)
        }
        return [...this.areas].sort((a, b) => {
            const aIndex = areaBookingOrder.findIndex(id => id === a.area.id)
            const bIndex = areaBookingOrder.findIndex(id => id === b.area.id)
            const aOrder = aIndex >= 0 ? aIndex : Number.MAX_SAFE_INTEGER
            const bOrder = bIndex >= 0 ? bIndex : Number.MAX_SAFE_INTEGER
            if (aOrder === bOrder) {
                return a.area.displayOrder - b.area.displayOrder
            }
            return aOrder - bOrder
        })
    }

    private areasWithBookingSlotsHiddenDueToCutOffTime(areaId?: string): AreaAvailability[] {
        const possibleAreas = areaId ? this.areas.filter(area => {
            return area.area.id === areaId
        }) : this.areas
        const bookingSlotsInsideCutOffTime = possibleAreas
            .flatMap(areaAvailability => {
                return areaAvailability.bookingSlotsInsideCutOffTime()
            })
        const bookingSlotsHiddenByCutOffTime = bookingSlotsInsideCutOffTime
            .filter(bookingSlotAvailability => {
                return possibleAreas.every(areaAvailability => {
                    return areaAvailability.area.cutOffTime.getTime() > bookingSlotAvailability.bookingSlot.dateTime.getTime()
                })
            })
        return possibleAreas
            .filter(areaAvailability => {
                return bookingSlotsHiddenByCutOffTime.some(bookingSlot => {
                    return areaAvailability.hasBookingSlot(bookingSlot.bookingSlot)
                })
            })
    }

    private allAreaOptionsPerBookingSlot() {
        const bookingSlots = this.areas
            .flatMap(areaAvailability => {
                return areaAvailability.bookingSlots.flatMap(bookingSlotAvailability => {
                    return bookingSlotAvailability.bookingSlot
                })
            })
            .filter((value, index, self) => {
                return self.indexOf(value) === index
            })
        return bookingSlots.map(bookingSlot => {
            return this.areas.flatMap(areaAvailability => {
                return areaAvailability.bookingSlots.find(bookingSlotAvailability => {
                    return bookingSlotAvailability.bookingSlot.dateTime.getTime() === bookingSlot.dateTime.getTime()
                })?.options ?? []
            })
        })
    }

    private configuredEventIds() {
        return this.areas.flatMap(areaAvailability => {
            return areaAvailability.bookingSlots.flatMap(bookingSlotAvailability => {
                return bookingSlotAvailability.options.map(option => {
                    return option.eventId
                })
            })
        })
            .filter(eventId => eventId !== null)
            .filter((value, index, self) => {
                return self.indexOf(value) === index
            })
    }
}
